import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _objectWithoutProperties from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className", "children", "viewport"],
  _excluded2 = ["className"],
  _excluded3 = ["className"],
  _excluded4 = ["className", "children"],
  _excluded5 = ["className", "onPointerDownOutside", "onFocusOutside"],
  _excluded6 = ["className"],
  _excluded7 = ["className"],
  _excluded8 = ["className"];
var __jsx = React.createElement;
import * as React from "react";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cn } from "@/common/lib/utils";

/**
 * shadcn-style wrapper around Radix Navigation Menu.
 *
 * Two deliberate deviations from the upstream shadcn recipe:
 *  - `NavigationMenuContent` is always `forceMount`ed so every mega-menu link
 *    ships in the initial SSR HTML (SEO requirement). Closed panels are hidden
 *    with `invisible` rather than `hidden`: the anchors stay crawlable, the
 *    panel stays laid out (so opening costs only compositing, not layout), and
 *    `visibility: hidden` still keeps it out of the tab order.
 *  - The floating `Viewport` is opt-in (`viewport` prop) because the Syarah
 *    header anchors each panel to its own trigger instead of a shared viewport.
 */

/**
 * Closed panels stay in the DOM and laid out; only paint state changes.
 *
 * A transition rather than Tailwind's `animate-fade-in`: that maps to a global
 * `@keyframes fadeIn`, which src/componentsShared/BottomSheet/Modal.css also
 * declares as a translateX slide, and the last global definition of a keyframe
 * name wins document-wide. A transition also eases out, not just in.
 *
 * `[transform:...]` is used instead of `translate-y-*`/`scale-*` so the value is
 * a plain declaration rather than Tailwind's composed transform variables.
 */
var panelMotionClasses = cn("origin-top transition-[opacity,transform,visibility] duration-200", "ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none", "data-[state=closed]:pointer-events-none data-[state=closed]:invisible", "data-[state=closed]:opacity-0 data-[state=closed]:[transform:translateY(-6px)_scale(0.98)]", "data-[state=open]:visible data-[state=open]:opacity-100 data-[state=open]:[transform:none]");
var NavigationMenu = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    _ref$viewport = _ref.viewport,
    viewport = _ref$viewport === void 0 ? true : _ref$viewport,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Root, _extends({
    ref: ref,
    "data-viewport": viewport,
    className: cn("relative flex max-w-max flex-1 items-center justify-center", className)
  }, props), children, viewport && /*#__PURE__*/React.createElement(NavigationMenuViewport, null));
});
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;
var NavigationMenuList = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    props = _objectWithoutProperties(_ref2, _excluded2);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.List, _extends({
    ref: ref,
    className: cn("group m-0 flex flex-1 list-none items-center justify-center gap-1 p-0", className)
  }, props));
});
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;
var NavigationMenuItem = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var className = _ref3.className,
    props = _objectWithoutProperties(_ref3, _excluded3);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Item, _extends({
    ref: ref,
    className: cn("relative", className)
  }, props));
});
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;
var navigationMenuTriggerStyle = function navigationMenuTriggerStyle() {
  return "inline-flex items-center justify-center gap-1 bg-transparent text-base font-medium text-navy-primary no-underline outline-none transition-colors hover:text-primary-new focus-visible:ring-2 focus-visible:ring-primary-new/40 disabled:pointer-events-none disabled:opacity-50";
};
var NavigationMenuTrigger = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
    children = _ref4.children,
    props = _objectWithoutProperties(_ref4, _excluded4);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Trigger, _extends({
    ref: ref,
    className: cn(navigationMenuTriggerStyle(), "group cursor-pointer border-0 p-0", className)
  }, props), children);
});
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;
var NavigationMenuContent = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    _onPointerDownOutside = _ref5.onPointerDownOutside,
    _onFocusOutside = _ref5.onFocusOutside,
    props = _objectWithoutProperties(_ref5, _excluded5);
  var contentRef = React.useRef(null);
  var setRefs = React.useCallback(function (node) {
    contentRef.current = node;
    if (typeof ref === "function") ref(node);else if (ref) ref.current = node;
  }, [ref]);

  /**
   * Every panel is `forceMount`ed for SEO, so every panel keeps a live
   * DismissableLayer — including the closed ones. A pointerdown inside the panel
   * the user actually opened counts as "outside" for its two closed siblings,
   * whose layers then dismiss the whole menu on pointerdown. The panel vanished
   * before mouseup, so no `click` ever fired and links did nothing.
   *
   * Closed panels have no business dismissing anything: preventing the event
   * stops DismissableLayer from calling onDismiss (`if (!event.defaultPrevented)`).
   */
  var isClosed = function isClosed() {
    var _contentRef$current;
    return ((_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 ? void 0 : _contentRef$current.getAttribute("data-state")) === "closed";
  };
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Content, _extends({
    ref: setRefs,
    forceMount: true,
    onPointerDownOutside: function onPointerDownOutside(event) {
      if (isClosed()) {
        event.preventDefault();
        return;
      }
      _onPointerDownOutside === null || _onPointerDownOutside === void 0 ? void 0 : _onPointerDownOutside(event);
    },
    onFocusOutside: function onFocusOutside(event) {
      if (isClosed()) {
        event.preventDefault();
        return;
      }
      _onFocusOutside === null || _onFocusOutside === void 0 ? void 0 : _onFocusOutside(event);
    },
    className: cn(panelMotionClasses, "top-full z-[10000] w-auto", className)
  }, props));
});
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;
var NavigationMenuLink = /*#__PURE__*/React.forwardRef(function (_ref6, ref) {
  var className = _ref6.className,
    props = _objectWithoutProperties(_ref6, _excluded6);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Link, _extends({
    ref: ref
    /* No layout utilities here: with `asChild` Radix's Slot concatenates this
       string with the child's own classes instead of merging them, so a
       flex-direction set here would fight the consumer's. */,
    className: cn("no-underline outline-none transition-colors", className)
  }, props));
});
NavigationMenuLink.displayName = NavigationMenuPrimitive.Link.displayName;
var NavigationMenuViewport = /*#__PURE__*/React.forwardRef(function (_ref7, ref) {
  var className = _ref7.className,
    props = _objectWithoutProperties(_ref7, _excluded7);
  return /*#__PURE__*/React.createElement("div", {
    className: "absolute top-full start-0 z-[10000] flex justify-center"
  }, /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Viewport, _extends({
    ref: ref,
    className: cn("relative mt-2 h-[var(--radix-navigation-menu-viewport-height)] w-full origin-top overflow-hidden rounded-3xl bg-white shadow-[0_8px_30px_0_rgba(0,20,60,0.12)] md:w-[var(--radix-navigation-menu-viewport-width)]", panelMotionClasses, className)
  }, props)));
});
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;
var NavigationMenuIndicator = /*#__PURE__*/React.forwardRef(function (_ref8, ref) {
  var className = _ref8.className,
    props = _objectWithoutProperties(_ref8, _excluded8);
  return /*#__PURE__*/React.createElement(NavigationMenuPrimitive.Indicator, _extends({
    ref: ref,
    className: cn("top-full z-[10001] flex h-1.5 items-end justify-center overflow-hidden data-[state=hidden]:hidden", className)
  }, props), /*#__PURE__*/React.createElement("div", {
    className: "relative top-[60%] size-2 rotate-45 rounded-tl-sm bg-white shadow-[0_0_8px_0_rgba(0,20,60,0.12)]"
  }));
});
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName;
export { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuLink, NavigationMenuIndicator, NavigationMenuViewport, navigationMenuTriggerStyle };